// Carga de datos del Informe Gerencial (Fase A — Robotpack 2026-09).
//
// Todo sale de endpoints EXISTENTES del coordinador; la comparación de
// períodos se resuelve con DOS llamadas por endpoint y merge acá (decisión de
// la fase A: cero cambios de backend para comparar). Facturación también va en
// dos llamadas (período y comparación por separado) y se agrupa por mes/área en
// FE con q_key (la OP factura una vez en el Q de su cierre).
//
// Resiliencia: cada endpoint se resuelve por separado — una sección que falla
// se marca "sin datos" y NO tumba el informe (el resto se genera igual).

import {
  coordinadorAnalisisLineasSchema,
  coordinadorAnalisisParosSchema,
  coordinadorAnalisisPersonalSchema,
  coordinadorCumplimientoSchema,
  coordinadorEntradaPitsSchema,
  facturacionQSchema,
  nivelServicioSchema,
  type CoordinadorAnalisisLineas,
  type CoordinadorAnalisisParos,
  type CoordinadorAnalisisPersonal,
  type CoordinadorCumplimiento,
  type CoordinadorEntradaPits,
  type FacturacionQ,
  type NivelServicio,
} from 'schemas';
import { apiGet } from '../../lib/api/client';

// =============================================================================
// Secciones del informe
// =============================================================================

export type SeccionInforme =
  | 'resumen'
  | 'facturacion'
  | 'nivel'
  | 'productividad'
  | 'lideres'
  | 'paros'
  | 'pits'
  | 'costos';

export const SECCIONES_INFORME: ReadonlyArray<{
  id: SeccionInforme;
  titulo: string;
  detalle: string;
}> = [
  { id: 'resumen', titulo: 'Resumen ejecutivo', detalle: 'KPIs del período con variación' },
  { id: 'facturacion', titulo: 'Facturación', detalle: 'Valor facturado por mes y área vs meta' },
  {
    id: 'nivel',
    titulo: 'Nivel de servicio',
    detalle: 'Programado vs realizado por semana, cumplimiento real y ácido',
  },
  {
    id: 'productividad',
    titulo: 'Productividad por área',
    detalle: 'Cumplimiento de meta por área, con comparativo',
  },
  {
    id: 'lideres',
    titulo: 'Cumplimiento de líderes',
    detalle: 'Ranking producido vs esperado por líder',
  },
  {
    id: 'paros',
    titulo: 'Análisis de paros',
    detalle: 'Paro total registrado (incluye programados) y pérdida $ donde afecta disponibilidad',
  },
  { id: 'pits', titulo: 'Entrada a Pits', detalle: 'Minutos por cambio vs meta, por causal' },
  { id: 'costos', titulo: 'Costos', detalle: 'Valor producido, mano de obra y utilidad por área' },
];

// =============================================================================
// Períodos
// =============================================================================

export interface Periodo {
  desde: string;
  hasta: string;
}

export type ResultadoSeccion<T> = { ok: true; data: T } | { ok: false };

/** Todo lo que el generador necesita. `comp` = período de comparación (o null). */
export interface DatosInformeGerencial {
  periodo: Periodo;
  comp: Periodo | null;
  cumplimiento: ResultadoSeccion<CoordinadorCumplimiento>;
  cumplimientoComp: ResultadoSeccion<CoordinadorCumplimiento> | null;
  nivel: ResultadoSeccion<NivelServicio>;
  /**
   * Facturación del PERÍODO del informe. Antes era una sola llamada al rango
   * completo (período + comparación): el KPI "valor total facturado" cambiaba al
   * activar la comparación (jul+ago vs solo ago) — el mismo período daba otro
   * número. Ahora el período y la comparación van por separado, como el resto.
   */
  facturacion: ResultadoSeccion<FacturacionQ>;
  facturacionComp: ResultadoSeccion<FacturacionQ> | null;
  paros: ResultadoSeccion<CoordinadorAnalisisParos>;
  parosComp: ResultadoSeccion<CoordinadorAnalisisParos> | null;
  lineas: ResultadoSeccion<CoordinadorAnalisisLineas>;
  personal: ResultadoSeccion<CoordinadorAnalisisPersonal>;
  pits: ResultadoSeccion<CoordinadorEntradaPits>;
  pitsComp: ResultadoSeccion<CoordinadorEntradaPits> | null;
}

// =============================================================================
// Helper de concurrencia (sin dependencias): corre las tareas de a `limite`.
// =============================================================================

export async function conLimite<T>(tareas: Array<() => Promise<T>>, limite = 4): Promise<T[]> {
  const resultados: T[] = new Array<T>(tareas.length);
  let siguiente = 0;
  const worker = async (): Promise<void> => {
    while (siguiente < tareas.length) {
      const i = siguiente++;
      resultados[i] = await tareas[i]!();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limite, tareas.length) }, worker));
  return resultados;
}

// =============================================================================
// Fetchers (rango → response). Cada uno atrapa su error → { ok: false }.
// =============================================================================

// Exportados para el fetch GRANULAR del PPTX (informeGerencialPptxDatos.ts):
// mismos fetchers con filtros extra (area_id / linea_id) por mes.
export const qs = (p: Periodo, extra?: Record<string, string>): string => {
  const params = new URLSearchParams({ desde: p.desde, hasta: p.hasta, ...(extra ?? {}) });
  return `?${params.toString()}`;
};

export async function seguro<T>(fn: () => Promise<T>): Promise<ResultadoSeccion<T>> {
  try {
    return { ok: true, data: await fn() };
  } catch {
    return { ok: false };
  }
}

/** Cumplimiento acotado a un ÁREA y un mes, con detalle por línea (PPTX). */
export const getCumplimientoArea = (p: Periodo, areaId: string) =>
  seguro(() =>
    apiGet(
      // base=validada (V1): el PPTX comparte la promesa del informe — la
      // productividad por línea sale de producción validada, igual que el PDF.
      `/api/coordinador/cumplimiento${qs(p, { agrupacion: 'linea', granularidad: 'mes', area_id: areaId, base: 'validada' })}`,
      coordinadorCumplimientoSchema,
    ),
  );

/** Análisis de personal acotado a UNA línea (PPTX — slide de líderes). */
export const getPersonalLinea = (p: Periodo, lineaId: string) =>
  seguro(() =>
    apiGet(
      `/api/coordinador/analisis-personal${qs(p, { linea_id: lineaId })}`,
      coordinadorAnalisisPersonalSchema,
    ),
  );

/** Análisis de paros acotado a UNA línea y un mes (PPTX — slide de paradas). */
export const getParosLinea = (p: Periodo, lineaId: string) =>
  seguro(() =>
    apiGet(
      `/api/coordinador/analisis-paros${qs(p, { linea_id: lineaId })}`,
      coordinadorAnalisisParosSchema,
    ),
  );

const getCumplimiento = (p: Periodo) =>
  seguro(() =>
    apiGet(
      // agrupacion=area: la productividad del informe es por área (el programa
      // de Alpina vive a nivel área, decisión C5); granularidad semana para la
      // serie. utilidad_por_area viene siempre que el usuario vea costos.
      // base=validada (V1): el informe promete "solo información validada por
      // supervisión" — producción y cumplimiento salen de validado_at, sin el
      // carril de tiendas (que no tiene validación). Paros/pits/personal
      // conservan su base operativa y el informe la declara.
      `/api/coordinador/cumplimiento${qs(p, { agrupacion: 'area', granularidad: 'semana', base: 'validada' })}`,
      coordinadorCumplimientoSchema,
    ),
  );

const getNivel = (p: Periodo) =>
  seguro(() => apiGet(`/api/coordinador/facturacion/nivel-servicio${qs(p)}`, nivelServicioSchema));

const getFacturacion = (p: Periodo) =>
  seguro(() => apiGet(`/api/coordinador/facturacion/q${qs(p)}`, facturacionQSchema));

const getParos = (p: Periodo) =>
  seguro(() => apiGet(`/api/coordinador/analisis-paros${qs(p)}`, coordinadorAnalisisParosSchema));

const getLineas = (p: Periodo) =>
  seguro(() => apiGet(`/api/coordinador/analisis-lineas${qs(p)}`, coordinadorAnalisisLineasSchema));

const getPersonal = (p: Periodo) =>
  seguro(() =>
    apiGet(`/api/coordinador/analisis-personal${qs(p)}`, coordinadorAnalisisPersonalSchema),
  );

const getPits = (p: Periodo) =>
  seguro(() => apiGet(`/api/coordinador/entrada-pits${qs(p)}`, coordinadorEntradaPitsSchema));

// =============================================================================
// Loader principal
// =============================================================================

/**
 * Carga los endpoints que las secciones seleccionadas necesitan (períodos
 * actual + comparación), con concurrencia acotada a 4. Endpoints que ninguna
 * sección seleccionada usa NO se llaman.
 */
export async function cargarDatosInforme(
  periodo: Periodo,
  comp: Periodo | null,
  secciones: ReadonlySet<SeccionInforme>,
): Promise<DatosInformeGerencial> {
  const sel = (...ids: SeccionInforme[]): boolean => ids.some((id) => secciones.has(id));

  const necesitaCumplimiento = sel('resumen', 'productividad', 'costos');
  const necesitaNivel = sel('resumen', 'nivel');
  const necesitaFacturacion = sel('facturacion');
  const necesitaParos = sel('resumen', 'paros');
  const necesitaLineas = sel('paros', 'costos');
  const necesitaPersonal = sel('lideres');
  const necesitaPits = sel('pits');

  const noCargada = { ok: false as const };
  let cumplimiento: ResultadoSeccion<CoordinadorCumplimiento> = noCargada;
  let cumplimientoComp: ResultadoSeccion<CoordinadorCumplimiento> | null = null;
  let nivel: ResultadoSeccion<NivelServicio> = noCargada;
  let facturacion: ResultadoSeccion<FacturacionQ> = noCargada;
  let facturacionComp: ResultadoSeccion<FacturacionQ> | null = null;
  let paros: ResultadoSeccion<CoordinadorAnalisisParos> = noCargada;
  let parosComp: ResultadoSeccion<CoordinadorAnalisisParos> | null = null;
  let lineas: ResultadoSeccion<CoordinadorAnalisisLineas> = noCargada;
  let personal: ResultadoSeccion<CoordinadorAnalisisPersonal> = noCargada;
  let pits: ResultadoSeccion<CoordinadorEntradaPits> = noCargada;
  let pitsComp: ResultadoSeccion<CoordinadorEntradaPits> | null = null;

  const tareas: Array<() => Promise<void>> = [];
  if (necesitaCumplimiento) {
    tareas.push(async () => {
      cumplimiento = await getCumplimiento(periodo);
    });
    if (comp) {
      tareas.push(async () => {
        cumplimientoComp = await getCumplimiento(comp);
      });
    }
  }
  if (necesitaNivel) {
    tareas.push(async () => {
      nivel = await getNivel(periodo);
    });
  }
  if (necesitaFacturacion) {
    tareas.push(async () => {
      facturacion = await getFacturacion(periodo);
    });
    if (comp) {
      tareas.push(async () => {
        facturacionComp = await getFacturacion(comp);
      });
    }
  }
  if (necesitaParos) {
    tareas.push(async () => {
      paros = await getParos(periodo);
    });
    if (comp) {
      tareas.push(async () => {
        parosComp = await getParos(comp);
      });
    }
  }
  if (necesitaLineas) {
    tareas.push(async () => {
      lineas = await getLineas(periodo);
    });
  }
  if (necesitaPersonal) {
    tareas.push(async () => {
      personal = await getPersonal(periodo);
    });
  }
  if (necesitaPits) {
    tareas.push(async () => {
      pits = await getPits(periodo);
    });
    if (comp) {
      tareas.push(async () => {
        pitsComp = await getPits(comp);
      });
    }
  }

  await conLimite(tareas, 4);

  return {
    periodo,
    comp,
    cumplimiento,
    cumplimientoComp,
    nivel,
    facturacion,
    facturacionComp,
    paros,
    parosComp,
    lineas,
    personal,
    pits,
    pitsComp,
  };
}

// =============================================================================
// Costos del informe (DN9): UNA sola lectura para PDF y PPTX
// =============================================================================
//
// Consolida líneas + tiendas con la MISMA fórmula del directivo, declara la base
// real, y concilia validado ↔ capturado cuando la base es validada:
//   validado (informe) + pendiente de validar = capturado (dashboard del director).
// Lo sin tarifa y lo sin costo de MO se declaran, no se suman. Probada en
// tests/features/coordinador/informe-costos.test.ts.

export interface CostosInformeFila {
  nombre: string;
  valor_producido: number;
  costo_mano_obra: number;
  utilidad: number;
  utilidad_pct: number | null;
}

export interface CostosInforme {
  baseLabel: 'producción validada' | 'producción capturada';
  valor_producido: number;
  costo_mano_obra: number;
  utilidad: number;
  utilidad_pct: number | null;
  /** Áreas de líneas ordenadas por utilidad + fila 'Tiendas' si hay plata de tiendas. */
  filas: CostosInformeFila[];
  /** Solo con base validada: lo que falta por validar (líneas + tiendas), misma fórmula. */
  pendiente: {
    paquetes: number;
    valor_producido: number;
    costo_mano_obra: number;
    utilidad: number;
  } | null;
  /** validado + pendiente = lo que ve el directivo (capturado). null si la base ya es capturada. */
  capturado_equivalente: {
    valor_producido: number;
    costo_mano_obra: number;
    utilidad: number;
  } | null;
  sin_tarifa: { paquetes: number; skus: number; costo_mano_obra: number };
  sin_costo_mo: { paquetes: number; valor_producido: number };
  /** Frase de base + conciliación, lista para pie de página. */
  nota_base: string;
  /** Frase de exclusiones (sin tarifa / sin costo MO) o de "todo con tarifa". */
  nota_exclusiones: string;
}

const r2 = (n: number) => Number(n.toFixed(2));
const milesEs = (n: number) => Math.round(n).toLocaleString('es-CO');
const copMes = (n: number) =>
  `$${(n / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;

export function resumenCostosInforme(c: CoordinadorCumplimiento): CostosInforme | null {
  const t = c.totales;
  if (
    t.valor_producido_total == null ||
    t.costo_mano_obra_total == null ||
    t.utilidad_total == null
  ) {
    return null;
  }
  const rt = c.rentabilidad_tiendas?.total ?? null;
  const conTiendas = rt !== null && rt.valor_producido + rt.costo_mano_obra > 0;
  const valor = r2(t.valor_producido_total + (conTiendas ? rt.valor_producido : 0));
  const costo = r2(t.costo_mano_obra_total + (conTiendas ? rt.costo_mano_obra : 0));
  const utilidad = r2(valor - costo);
  const filas: CostosInformeFila[] = (c.utilidad_por_area ?? [])
    .map((a) => ({
      nombre: a.area_nombre,
      valor_producido: a.valor_producido,
      costo_mano_obra: a.costo_mano_obra,
      utilidad: a.utilidad,
      utilidad_pct: a.utilidad_pct,
    }))
    .sort((a, b) => b.utilidad - a.utilidad);
  if (conTiendas) {
    filas.push({
      nombre: 'Tiendas (reempaque + insumos cobrados)',
      valor_producido: rt.valor_producido,
      costo_mano_obra: rt.costo_mano_obra,
      utilidad: rt.utilidad,
      utilidad_pct: rt.utilidad_pct,
    });
  }
  const baseLabel = t.base_utilidad === 'validada' ? 'producción validada' : 'producción capturada';
  const pL = t.pendiente_validacion_utilidad ?? null;
  const pT = c.rentabilidad_tiendas?.pendiente_validacion ?? null;
  const pendiente =
    baseLabel === 'producción validada' && (pL || pT)
      ? {
          paquetes: r2((pL?.paquetes ?? 0) + (pT?.paquetes ?? 0)),
          valor_producido: r2((pL?.valor_producido ?? 0) + (pT?.valor_producido ?? 0)),
          costo_mano_obra: r2((pL?.costo_mano_obra ?? 0) + (pT?.costo_mano_obra ?? 0)),
          utilidad: r2((pL?.utilidad ?? 0) + (pT?.utilidad ?? 0)),
        }
      : null;
  const capturado_equivalente = pendiente
    ? {
        valor_producido: r2(valor + pendiente.valor_producido),
        costo_mano_obra: r2(costo + pendiente.costo_mano_obra),
        utilidad: r2(utilidad + pendiente.utilidad),
      }
    : null;
  const st = t.sin_tarifa_total ?? null;
  const stT = rt?.sin_tarifa ?? null;
  const sm = t.sin_costo_mo_total ?? null;
  const smT = rt?.sin_costo_mo ?? null;
  const sin_tarifa = {
    paquetes: r2((st?.paquetes ?? 0) + (stT?.paquetes ?? 0)),
    skus: (st?.skus ?? 0) + (stT?.skus ?? 0),
    costo_mano_obra: r2((st?.costo_mano_obra ?? 0) + (stT?.costo_mano_obra ?? 0)),
  };
  const sin_costo_mo = {
    paquetes: r2((sm?.paquetes ?? 0) + (smT?.paquetes ?? 0)),
    valor_producido: r2((sm?.valor_producido ?? 0) + (smT?.valor_producido ?? 0)),
  };
  const nota_base =
    `Base: ${baseLabel}, líneas + tiendas, misma fórmula que el dashboard del directivo (precio vigente × paquetes; MO = operarios × horas reales × costo hora-persona).` +
    (pendiente && capturado_equivalente
      ? ` Pendiente de validar: ${milesEs(pendiente.paquetes)} paq → ${copMes(pendiente.valor_producido)} de valor y ${copMes(pendiente.costo_mano_obra)} de MO. Validado + pendiente = ${copMes(capturado_equivalente.valor_producido)} / ${copMes(capturado_equivalente.costo_mano_obra)} / utilidad ${copMes(capturado_equivalente.utilidad)}, que es lo que muestra el directivo (capturado).`
      : '');
  const partes: string[] = [];
  if (sin_tarifa.paquetes > 0) {
    partes.push(
      `sin tarifa ${milesEs(sin_tarifa.paquetes)} paq en ${sin_tarifa.skus} SKU(s) con ${copMes(sin_tarifa.costo_mano_obra)} de MO sin asignar`,
    );
  }
  if (sin_costo_mo.paquetes > 0) {
    partes.push(
      `sin costo hora-persona ${milesEs(sin_costo_mo.paquetes)} paq por ${copMes(sin_costo_mo.valor_producido)} de valor`,
    );
  }
  const nota_exclusiones =
    partes.length > 0
      ? `Fuera del cálculo (no se muestran como pérdida ni como utilidad): ${partes.join(' · ')}.`
      : 'Toda la producción del período tiene precio y costo de mano de obra vigentes.';
  return {
    baseLabel,
    valor_producido: valor,
    costo_mano_obra: costo,
    utilidad,
    utilidad_pct: valor > 0 ? r2((utilidad / valor) * 100) : null,
    filas,
    pendiente,
    capturado_equivalente,
    sin_tarifa,
    sin_costo_mo,
    nota_base,
    nota_exclusiones,
  };
}

/**
 * Recorta una tabla a `max` filas y agrega el resto en 'Otras (N)': el total de
 * la tabla siempre concilia con las filas mostradas.
 */
export function filasConOtras<T extends CostosInformeFila>(
  filas: T[],
  max: number,
): CostosInformeFila[] {
  if (filas.length <= max) return filas;
  const visibles: CostosInformeFila[] = filas.slice(0, max - 1);
  const resto = filas.slice(max - 1);
  const valor = r2(resto.reduce((s, f) => s + f.valor_producido, 0));
  const costo = r2(resto.reduce((s, f) => s + f.costo_mano_obra, 0));
  const utilidad = r2(resto.reduce((s, f) => s + f.utilidad, 0));
  visibles.push({
    nombre: `Otras (${resto.length})`,
    valor_producido: valor,
    costo_mano_obra: costo,
    utilidad,
    utilidad_pct: valor > 0 ? r2((utilidad / valor) * 100) : null,
  });
  return visibles;
}

// =============================================================================
// Facturación: agrupación por mes para la tabla del PDF
// =============================================================================

export interface FacturacionMesAgrupado {
  /** 'YYYY-MM' */
  mes: string;
  unidades: number;
  valor: number;
  /** true = fila del período de COMPARACIÓN (no suma al total del informe). */
  comp: boolean;
}

/**
 * Agrupa las filas de facturación por mes SEPARANDO período actual y comparación.
 * Antes la clave era solo 'YYYY-MM': al comparar dos semanas del mismo mes, las
 * dos caían en una fila y se sumaban. Ahora una misma clave de mes puede aparecer
 * dos veces (comp. y actual), ordenadas por mes y con la comparación primero.
 */
export function agruparFacturacionPorMes(
  actual: ReadonlyArray<{ q_key: string; cantidad_validada: number; valor_total: number }>,
  comp: ReadonlyArray<{ q_key: string; cantidad_validada: number; valor_total: number }> | null,
): FacturacionMesAgrupado[] {
  const porClave = new Map<string, FacturacionMesAgrupado>();
  const acumular = (
    filas: ReadonlyArray<{ q_key: string; cantidad_validada: number; valor_total: number }>,
    esComp: boolean,
  ) => {
    for (const fila of filas) {
      const mes = fila.q_key.slice(0, 7);
      const clave = `${esComp ? 'C' : 'A'}|${mes}`;
      const acc = porClave.get(clave) ?? { mes, unidades: 0, valor: 0, comp: esComp };
      acc.unidades += fila.cantidad_validada;
      acc.valor += fila.valor_total;
      porClave.set(clave, acc);
    }
  };
  if (comp) acumular(comp, true);
  acumular(actual, false);
  return [...porClave.values()].sort(
    (a, b) => a.mes.localeCompare(b.mes) || Number(b.comp) - Number(a.comp),
  );
}

// =============================================================================
// Comparación de períodos
//
// El PERÍODO del informe lo resuelve el selector compartido (lib/periodo, el
// mismo del Dashboard y de Análisis de paros, persistido en la URL). Acá solo
// vive la resolución del período de COMPARACIÓN.
// =============================================================================

export type ModoComparacion = 'ninguno' | 'anterior' | 'mes_anterior' | 'personalizado';

const iso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

/**
 * Período de comparación según el modo.
 *
 * - `anterior`: el que entrega el selector compartido "a la misma altura"
 *   (DN8): si el período está en curso, compara to-date vs to-date del período
 *   anterior; si está cerrado, completo vs completo. Antes se comparaba "mismo
 *   largo, inmediatamente anterior": 1–7 de sep vs 25–31 de ago, que no es la
 *   misma altura del mes.
 * - `mes_anterior`: el mes calendario anterior al inicio del período, completo.
 * - `personalizado`: lo que el usuario escribió (o null si está incompleto).
 */
export function periodoComparacion(
  modo: ModoComparacion,
  periodo: Periodo,
  personalizado: Periodo | null,
  anteriorMismaAltura: Periodo,
): Periodo | null {
  switch (modo) {
    case 'ninguno':
      return null;
    case 'anterior':
      return anteriorMismaAltura;
    case 'mes_anterior': {
      const d = new Date(periodo.desde + 'T00:00:00');
      return {
        desde: iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)),
        hasta: iso(new Date(d.getFullYear(), d.getMonth(), 0)),
      };
    }
    case 'personalizado':
      return personalizado;
  }
}

/**
 * Cómo se etiqueta la comparación (DN8: decir a qué altura se compara).
 * `parcial` = el período actual está en curso (to-date).
 */
export function etiquetaComparacion(modo: ModoComparacion, parcial: boolean): string | null {
  switch (modo) {
    case 'ninguno':
      return null;
    case 'anterior':
      return parcial
        ? 'vs. mismo corte del período anterior (a la misma altura)'
        : 'vs. período anterior completo';
    case 'mes_anterior':
      return parcial
        ? 'vs. mes anterior completo (el actual va parcial)'
        : 'vs. mes anterior completo';
    case 'personalizado':
      return 'vs. período elegido manualmente';
  }
}
